import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ParsedSchedule } from "../../../utils/parseScheduleExcel";

// ─── Types ────────────────────────────────────────────────────────────────────

export type UploadStatus =
  | "idle"
  | "parsed"
  | "under_review"
  | "approved"
  | "rejected"
  | "saving"
  | "saved"
  | "error";

export interface ScheduleUploadState {
  parsedData: ParsedSchedule | null;
  uploadStatus: UploadStatus;
  errorMessage: string | null;
  uploadedFileName: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectionNote: string | null;
  unmatchedEmployees: string[];
  isFetchingEmployees: boolean;
}

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState: ScheduleUploadState = {
  parsedData: null,
  uploadStatus: "idle",
  errorMessage: null,
  uploadedFileName: null,
  approvedBy: null,
  approvedAt: null,
  rejectionNote: null,
  unmatchedEmployees: [],
  isFetchingEmployees: false,
};

// ─── Slice ───────────────────────────────────────────────────────────────────

export const scheduleUploadSlice = createSlice({
  name: "scheduleUpload",
  initialState,
  reducers: {
    setParsedData: (state, action: PayloadAction<ParsedSchedule>) => {
      state.parsedData = action.payload;
      state.uploadedFileName = action.payload.fileName;
      state.uploadStatus = "parsed";
      state.errorMessage = null;
    },

    setUploadStatus: (state, action: PayloadAction<UploadStatus>) => {
      state.uploadStatus = action.payload;
    },

    setError: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
      state.uploadStatus = "error";
    },

    /** Remove an employee row from the parsed data */
    deleteRow: (state, action: PayloadAction<string>) => {
      if (state.parsedData) {
        state.parsedData.rows = state.parsedData.rows.filter(
          (row) => row.id !== action.payload
        );
      }
    },

    /** Clear a specific shift cell for an employee */
    clearShiftCell: (
      state,
      action: PayloadAction<{ rowId: string; weekIndex: number; dayIndex: number }>
    ) => {
      if (state.parsedData) {
        const { rowId, weekIndex, dayIndex } = action.payload;
        const row = state.parsedData.rows.find(r => r.id === rowId);
        if (row && row.weeks[weekIndex] && row.weeks[weekIndex].days[dayIndex]) {
          row.weeks[weekIndex].days[dayIndex].value = "-";
        }
      }
    },

    approveSchedule: (state, action: PayloadAction<{ approvedBy: string }>) => {
      state.uploadStatus = "approved";
      state.approvedBy = action.payload.approvedBy;
      state.approvedAt = new Date().toISOString();
      state.rejectionNote = null;
    },

    markSaving: (state) => {
      state.uploadStatus = "saving";
    },

    markSaved: (state) => {
      state.uploadStatus = "saved";
    },

    rejectSchedule: (state, action: PayloadAction<string | null>) => {
      state.uploadStatus = "rejected";
      state.rejectionNote = action.payload;
      state.approvedBy = null;
      state.approvedAt = null;
    },

    /**
     * Apply resolved employee_id mappings to parsedData rows.
     * Rows whose name has no match get employee_id = null.
     * Builds the unmatchedEmployees list from unresolved names.
     */
    setEmployeeMappings: (
      state,
      action: PayloadAction<{ mappings: Record<string, string | null> }>
    ) => {
      if (!state.parsedData) return;
      const { mappings } = action.payload;
      const unmatched: string[] = [];
      for (const row of state.parsedData.rows) {
        const resolvedId = mappings[row.employee] ?? null;
        row.employee_id = resolvedId;
        if (!resolvedId) unmatched.push(row.employee);
      }
      state.unmatchedEmployees = unmatched;
    },

    setFetchingEmployees: (state, action: PayloadAction<boolean>) => {
      state.isFetchingEmployees = action.payload;
    },

    resetUpload: () => initialState,
  },
});

export const {
  setParsedData,
  setUploadStatus,
  setError,
  deleteRow,
  clearShiftCell,
  approveSchedule,
  markSaving,
  markSaved,
  rejectSchedule,
  setEmployeeMappings,
  setFetchingEmployees,
  resetUpload,
} = scheduleUploadSlice.actions;

export default scheduleUploadSlice.reducer;
